import React, { useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
} from '@material-ui/core';
import KeyboardBackspaceOutlined from '@material-ui/icons/KeyboardBackspaceOutlined';
import { format } from 'date-fns';
import {
  AutoDebitSetupStatus,
  FeePaymentFrequency,
  IdLeaseAgreement,
  ITenantInfo,
  RentDueDay,
} from 'realhaus-sdk';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { AdminPageLayout, PageTitle } from '../../../components/uiComponents/UIComponents';
import { RootStoreContext } from '../../../global/storeContext';
import { paths } from '../../../routes';
import { StripePublishableKey } from '../../../appConfig';
import { IdentityVerificationGate } from '../../../components/gatekeeper/IdentityVerificationGate';

type SetupStatus =
  | 'loading'
  | 'idle'
  | 'submitting'
  | 'succeeded'
  | 'processing'
  | 'already-complete'
  | 'error';

const rentDueText = (value: RentDueDay | number): string => {
  if (!value) return 'First of month';
  switch (value) {
    case RentDueDay.FIRST_OF_MONTH:
      return 'First of month';
    case RentDueDay.END_OF_MONTH:
      return 'End of month';
    default:
      return `${value} of month`;
  }
};

/**
 * The fees Auto-Pay draws each month with rent (same rule as the server's isAutoPayBill): parking,
 * and pet/smoking fees the lease sets as monthly.
 */
const monthlyLeaseFees = (lease: IdLeaseAgreement): { label: string; amount: number }[] => {
  const fees = lease.fees;
  const items = [
    { label: 'Parking', amount: Number(fees?.parkingFee ?? 0), monthly: true },
    {
      label: 'Pet fee',
      amount: Number(fees?.petFee ?? 0),
      monthly: fees?.petFeeFrequency === FeePaymentFrequency.MONTHLY,
    },
    {
      label: 'Smoking fee',
      amount: Number(fees?.smokingFee ?? 0),
      monthly: fees?.smokingFeeFrequency === FeePaymentFrequency.MONTHLY,
    },
  ];
  return items
    .filter((f) => f.monthly && f.amount > 0)
    .map(({ label, amount }) => ({ label, amount }));
};

const formatAddress = (lease: IdLeaseAgreement): string => {
  const addr = lease.listingInfo?.address;
  if (!addr) return '';
  const line1 = [addr.unit, addr.streetAddress].filter(Boolean).join(' ');
  return [line1, addr.city, addr.province].filter(Boolean).join(', ');
};

const SetupAutoPayForm: React.FC<{
  billingName: string;
  billingEmail: string;
  onResult: (status: SetupStatus, errorMessage?: string) => void;
  status: SetupStatus;
}> = observer(({ billingName, billingEmail, onResult, status }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { uiStore } = useContext(RootStoreContext);
  const [elementReady, setElementReady] = useState(false);

  const isSubmitting = status === 'submitting';
  const canSubmit = !!stripe && !!elements && elementReady && !isSubmitting;

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    onResult('submitting');
    uiStore.showLoading();
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onResult('error', submitError.message ?? 'Please correct the highlighted fields.');
        return;
      }

      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: { name: billingName, email: billingEmail },
          },
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        onResult('error', error.message ?? 'Failed to confirm Auto-Pay setup.');
        return;
      }

      switch (setupIntent?.status) {
        case 'succeeded':
          onResult('succeeded');
          uiStore.success('Auto-Pay setup complete');
          return;
        case 'processing':
          onResult('processing');
          uiStore.success('Auto-Pay setup is being verified.');
          return;
        case 'requires_action':
          onResult('error', 'Additional verification is required to complete Auto-Pay setup.');
          return;
        default:
          onResult('error', 'Auto-Pay setup did not complete. Please try again.');
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Something went wrong.';
      onResult('error', message);
    } finally {
      uiStore.hideLoading();
    }
  };

  return (
    <Box>
      <PaymentElement
        onReady={() => setElementReady(true)}
        options={{
          defaultValues: {
            billingDetails: { name: billingName, email: billingEmail },
          },
        }}
      />
      <Box mt={3}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={!canSubmit}
          fullWidth
        >
          {isSubmitting ? 'Confirming...' : 'Confirm & Authorize'}
        </Button>
      </Box>
    </Box>
  );
});

const StatusPanel: React.FC<{
  status: SetupStatus;
  errorMessage?: string;
  onReturn: () => void;
  onTryAgain: () => void;
}> = ({ status, errorMessage, onReturn, onTryAgain }) => {
  switch (status) {
    case 'succeeded':
      return (
        <Box>
          <Typography variant='h6' style={{ color: '#2e7d32' }} gutterBottom>
            Auto-Pay set up successfully
          </Typography>
          <Typography variant='body1' gutterBottom>
            Your bank account is connected. Rent and monthly lease fees will be auto-debited each
            month.
            Status may take a moment to reflect on your lease.
          </Typography>
          <Box mt={3}>
            <Button variant='contained' color='primary' onClick={onReturn}>
              Return to Lease
            </Button>
          </Box>
        </Box>
      );
    case 'processing':
      return (
        <Box>
          <Typography variant='h6' gutterBottom>
            Verifying your bank account
          </Typography>
          <Typography variant='body1' gutterBottom>
            We're confirming your bank details with your financial institution.
            This can take 4-5 business days. You'll get a notification when it's done.
          </Typography>
          <Box mt={3}>
            <Button variant='contained' color='primary' onClick={onReturn}>
              Return to Lease
            </Button>
          </Box>
        </Box>
      );
    case 'already-complete':
      return (
        <Box>
          <Typography variant='h6' gutterBottom>
            Auto-Pay is already set up for this lease
          </Typography>
          <Box mt={3}>
            <Button variant='contained' color='primary' onClick={onReturn}>
              Return to Lease
            </Button>
          </Box>
        </Box>
      );
    case 'error':
      return (
        <Box>
          <Typography variant='h6' style={{ color: '#c62828' }} gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant='body1' gutterBottom>
            {errorMessage ?? 'Auto-Pay setup did not complete. Please try again.'}
          </Typography>
          <Box mt={3} display='flex' gridGap={12}>
            <Button variant='contained' color='primary' onClick={onTryAgain}>
              Try Again
            </Button>
            <Button variant='outlined' onClick={onReturn}>
              Return to Lease
            </Button>
          </Box>
        </Box>
      );
    default:
      return null;
  }
};

const SetupAutoPay: React.FC = observer(() => {
  const { leaseId } = useParams<{ leaseId: string }>();
  const navigate = useNavigate();
  const { leaseStore, bankAccountStore, userStore, tenantStore, uiStore } =
    useContext(RootStoreContext);

  const [lease, setLease] = useState<IdLeaseAgreement | undefined>();
  const [billing, setBilling] = useState<{ name: string; email: string } | undefined>();
  const [clientSecret, setClientSecret] = useState<string | undefined>();
  const [status, setStatus] = useState<SetupStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [stripePromise] = useState<Promise<Stripe | null>>(() =>
    loadStripe(StripePublishableKey ?? ''),
  );

  const returnToLease = () => {
    if (!leaseId) {
      navigate(paths.tenant.leases, { replace: true });
      return;
    }
    navigate(`${paths.tenant.leases}?leaseId=${leaseId}`);
  };

  useEffect(() => {
    if (!leaseId) {
      navigate(paths.tenant.leases, { replace: true });
      return;
    }

    let cancelled = false;
    const bootstrap = async () => {
      try {
        const fetchedLease = await leaseStore.getLeaseAgreementById(leaseId);
        if (cancelled) return;

        if (!fetchedLease) {
          uiStore.error('Lease not found');
          navigate(paths.tenant.leases, { replace: true });
          return;
        }

        const currentUserId = userStore.userInfo?.uid;
        if (!currentUserId || !fetchedLease.tenantIds.includes(currentUserId)) {
          uiStore.error('You are not authorized to set up Auto-Pay for this lease');
          navigate(paths.tenant.leases, { replace: true });
          return;
        }

        if (fetchedLease.policies?.autoPaymentSetup !== true) {
          uiStore.error('Auto-Pay is not enabled for this lease');
          navigate(paths.tenant.leases, { replace: true });
          return;
        }

        setLease(fetchedLease);

        const profile = await tenantStore.getProfile(currentUserId);
        const userBio = await tenantStore.getUserBio(currentUserId);
        if (cancelled) return;

        const tenantInfo = { ...profile, ...userBio } as ITenantInfo;
        setBilling({
          name: `${tenantInfo.firstname ?? ''} ${tenantInfo.lastname ?? ''}`.trim(),
          email: userStore.userInfo?.email ?? '',
        });

        if (fetchedLease.autoDebitSetup?.status === AutoDebitSetupStatus.COMPLETED) {
          setStatus('already-complete');
          return;
        }

        const secret = await bankAccountStore.createPADSetup(leaseId);
        if (cancelled) return;
        if (secret.kind === 'already-complete') {
          setStatus('already-complete');
          return;
        }
        if (secret.kind === 'error') {
          setStatus('error');
          setErrorMessage('Could not start Auto-Pay setup. Please try again later.');
          return;
        }
        setClientSecret(secret.clientSecret);
        setStatus('idle');
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setErrorMessage(e instanceof Error ? e.message : 'Something went wrong.');
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [leaseId]);

  const handleResult = (next: SetupStatus, message?: string) => {
    setStatus(next);
    setErrorMessage(message);
  };

  const handleTryAgain = () => {
    setErrorMessage(undefined);
    setStatus('idle');
  };

  if (status === 'error' && (!lease || !billing)) {
    return (
      <StatusPanel
        status='error'
        errorMessage={errorMessage}
        onReturn={returnToLease}
        onTryAgain={handleTryAgain}
      />
    );
  }

  if (status === 'loading' || !lease || !billing) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='40vh'>
        <CircularProgress />
      </Box>
    );
  }

  const showForm = status === 'idle' || status === 'submitting';

  return (
    <Box>

      <Box mb={4}>
        <Link
          component='button'
          onClick={returnToLease}
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <KeyboardBackspaceOutlined fontSize='small' />
          <Box ml={0.5}>
            <b>Back to Lease</b>
          </Box>
        </Link>
      </Box>

      <Grid container item xs={12} spacing={5}>
        <Grid item xs={12} md={5}>
          <Typography variant='subtitle2' color='textSecondary' gutterBottom>
            LEASE
          </Typography>
          <Typography variant='body1'>{formatAddress(lease)}</Typography>
          <Box mt={2}>
            <Typography variant='subtitle2' color='textSecondary' gutterBottom>
              TERM
            </Typography>
            <Typography variant='body1'>
              {format(lease.moveinDate, 'PP')} to {format(lease.moveoutDate, 'PP')}
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography variant='subtitle2' color='textSecondary' gutterBottom>
              RENT
            </Typography>
            <Typography variant='body1'>
              $CAD {lease.rentAmount} / month, due {rentDueText(lease.dueRentDay)}
            </Typography>
          </Box>
          {monthlyLeaseFees(lease).length > 0 && (
            <Box mt={2}>
              <Typography variant='subtitle2' color='textSecondary' gutterBottom>
                MONTHLY FEES
              </Typography>
              {monthlyLeaseFees(lease).map((fee) => (
                <Typography variant='body1' key={fee.label}>
                  {fee.label}: $CAD {fee.amount} / month
                </Typography>
              ))}
            </Box>
          )}
          <Box mt={2}>
            <Typography variant='subtitle2' color='textSecondary' gutterBottom>
              SETTING UP FOR
            </Typography>
            <Typography variant='body1'>{billing.name}</Typography>
            <Typography variant='body2' color='textSecondary'>
              {billing.email}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          {showForm && clientSecret && (
            <Box>
              <Typography variant='body1' gutterBottom>
                Connect your Canadian bank account to authorize Pre-Authorized Debit (PAD).
                Rent and monthly lease fees will be drawn automatically on the due date each month.
                Other bills, such as deposits, are not drawn automatically; you pay them yourself.
              </Typography>
              <Box>
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    onComplete: () => {
                      setStatus('succeeded');
                      uiStore.success('Auto-Pay setup submitted');
                    },
                  }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </Box>
            </Box>
          )}

          {!showForm && (

            <StatusPanel
              status={status}
              errorMessage={errorMessage}
              onReturn={returnToLease}
              onTryAgain={handleTryAgain}
            />


          )}
        </Grid>
      </Grid>

      <Box mt={4}>
        <Divider />
      </Box>
    </Box>
  );
});

export const SetupAutoPayPage: React.FC = observer(() => {
  return (
    <AdminPageLayout
      header={<PageTitle>Setup Auto-Pay</PageTitle>}
      content={
        <IdentityVerificationGate>
          <SetupAutoPay />
        </IdentityVerificationGate>
      }
    />
  );
});

export default SetupAutoPayPage;
